/**
 * docStashAdjudication — the #2671 unsaved-content stash decision.
 *
 * DocEditorModal's mount GET adjudicates a stash left behind by a failed
 * save-on-close against the LIVE server copy. The three-way decision (and the
 * content/title normalization it depends on) is a pure function here so the
 * modal's effect stays thin: fetch → adjudicate → apply outcome. Decision
 * order:
 *
 *   1. `drop` — the stash equals the fresh server copy: failure-after-commit
 *      (the close PATCH landed server-side but its response errored
 *      client-side). Checked against the GET's fresh copy BEFORE any etag
 *      comparison: a committed-but-errored close PATCH advances the server
 *      etag, so the etag check alone would misread this case as a conflict.
 *      Nothing to restore — the modal just drops the stash.
 *   2. `restore` — the stash etag matches the live etag AND the editor is
 *      still untouched: the doc is on the same server version the failed
 *      close was against, so the buffered title/body may be applied (the
 *      etag-gated equivalent of the old synchronous
 *      restore-into-initial-state), counted as unsaved, and persisted through
 *      the modal's normal debounced autosave.
 *   3. `conflict` — anything else (etag differs, pre-etag stash, or the user
 *      already typed): NEVER touch the live body — preserve the stashed copy
 *      as a version-history recovery checkpoint. `updatedCheckpoints`
 *      prepends it to the freshest known checkpoint list; the modal PATCHes
 *      that array and keeps the stash if the PATCH fails so the next open
 *      retries.
 *
 * Side effects (state setters, toasts, timer arming, the checkpoint PATCH,
 * stash clearing, markStashSafeToClear) stay in DocEditorModal — this module
 * decides, the modal applies.
 */
import type { UnsavedDocStash } from './useDocSaveFailureBuffer';

// Checkpoint for version history. Owned here (not in DocEditorModal) so the
// conflict outcome can construct the recovery checkpoint without importing
// the modal — imports stay one-way: modal → adjudication → buffer.
export interface DocCheckpoint {
  id: string;
  label: string;
  content: string;
  createdAt: string;
  author?: 'ai' | 'user';
  type?: 'manual' | 'auto' | 'restore' | 'ai';
  parentVersionId?: string;
}

export function ensureHtmlLineBreaks(content: string): string {
  if (!content) return content;
  if (/<[a-z][\s\S]*>/i.test(content)) return content;
  // Convert literal \n sequences (from imported/ingested data) to real newlines
  content = content.replace(/\\n/g, '\n');
  let result = content;
  result = result.replace(/^### (.+)$/gm, '<h3>$1</h3>');
  result = result.replace(/^## (.+)$/gm, '<h2>$1</h2>');
  result = result.replace(/^# (.+)$/gm, '<h1>$1</h1>');
  result = result.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  result = result.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  result = result.replace(/_([^_]+)_/g, '<em>$1</em>');
  result = result.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');
  result = result.replace(/\n/g, '<br>');
  result = result.replace(/<br>(<\/?(?:h[1-6]|ul|ol|li|p|div)>)/g, '$1');
  result = result.replace(/(<\/?(?:h[1-6]|ul|ol|li|p|div)>)<br>/g, '$1');
  return result;
}

/** Auto-title: derive a title from the first line of content (like Google Docs). */
export function deriveTitle(html: string): string {
  // Convert block closers to newlines before stripping tags, so paragraphs/headings/<br> count as line breaks.
  // Without this, <p>foo</p><p>bar</p> collapses to "foobar" and the "first line" becomes the whole doc.
  const withBreaks = html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|h[1-6]|li|blockquote|pre)>/gi, '\n');
  const plain = withBreaks.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ');
  const firstLine = plain.split(/\n/).map((s) => s.trim()).find((s) => s.length > 0) || '';
  return firstLine.slice(0, 100);
}

export type DocStashOutcome =
  | { kind: 'drop' }
  | {
      kind: 'restore';
      /** Normalized stash title to apply ('' for Untitled). */
      title: string;
      /** Normalized stash body to apply. */
      body: string;
      titleManuallySet: boolean;
      /** Normalized fresh SERVER title/body — the "last saved" baseline, so the restored buffer counts as unsaved. */
      freshTitle: string;
      freshBody: string;
    }
  | { kind: 'conflict'; updatedCheckpoints: DocCheckpoint[] };

/**
 * Pure three-way stash decision. `editorUntouched` = the editor still equals
 * the initial (server-copy) state it opened with; `freshData`/`freshEtag` are
 * the mount GET's response; `currentCheckpoints` is the modal's latest known
 * checkpoint list, used only when the GET carried no checkpoint array.
 */
export function adjudicateDocStash(args: {
  stash: UnsavedDocStash;
  freshData: { title?: string; body?: string; content?: string } | undefined;
  freshEtag: string | null;
  freshCheckpoints: DocCheckpoint[] | null;
  currentCheckpoints: DocCheckpoint[];
  editorUntouched: boolean;
}): DocStashOutcome {
  const { stash, freshData, freshEtag, freshCheckpoints, currentCheckpoints, editorUntouched } = args;

  const freshBody = ensureHtmlLineBreaks(freshData?.body ?? freshData?.content ?? '');
  const rawFreshTitle = freshData?.title ?? '';
  const freshTitle = rawFreshTitle === 'Untitled' ? '' : rawFreshTitle;
  // `||` (not `??`) is intentional: autosave never persists a
  // deliberately-blanked title (`patch.title` is only sent when
  // `title.trim()`), so an empty stash title means "none captured" —
  // fall back to the server's.
  const rawStashTitle = stash.title || rawFreshTitle;
  const stashTitle = rawStashTitle === 'Untitled' ? '' : rawStashTitle;
  const stashBody = ensureHtmlLineBreaks(stash.body);

  // 1. Failure-after-commit equality short-circuit (see module doc).
  if (stashBody === freshBody && stashTitle === freshTitle) {
    return { kind: 'drop' };
  }

  // 2. Same server version the failed close was against + untouched editor →
  // restore the buffer.
  if (stash.etag && freshEtag && stash.etag === freshEtag && editorUntouched) {
    const isAutoStashTitle = !rawStashTitle || rawStashTitle === 'Untitled' || rawStashTitle === deriveTitle(stashBody);
    return {
      kind: 'restore',
      title: stashTitle,
      body: stashBody,
      titleManuallySet: !isAutoStashTitle,
      freshTitle,
      freshBody,
    };
  }

  // 3. Conflict: the doc changed since the failed close (or the stash
  // predates etag stamping, or the user already started editing the live
  // copy). Preserve the stashed copy in version history.
  const now = new Date();
  const dateLabel = now.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
    + ', ' + now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const recoveryCheckpoint: DocCheckpoint = {
    id: `cp-${Date.now()}`,
    label: `Recovered unsaved changes — ${dateLabel}`,
    content: stash.body,
    createdAt: now.toISOString(),
    author: 'user',
    type: 'auto',
  };
  return {
    kind: 'conflict',
    updatedCheckpoints: [recoveryCheckpoint, ...(freshCheckpoints ?? currentCheckpoints)],
  };
}
